import React, { useContext, useEffect, useState } from 'react';
import Helmet from 'react-helmet';
import {
  Box,
  createStyles,
  makeStyles,
  Theme,
  Container,
} from '@material-ui/core';
import { graphql } from 'gatsby';
// eslint-disable-next-line import/no-unassigned-import
import './css/contentful-pages.css';
import { LanguageContext } from '../providers/LanguageProvider';
import Categories from '../components/blog/categories';
import { useTranslation } from '../hooks/useTranslation';
import { BlogArticleFragment } from '../__generated__/gatsby-types';
import Carousel from '../components/blog/carousel';
import { useIsMobile } from '../util/style-utils';
import colors from '../themes/main-colors';
import RecentPosts from '../components/blog/recent-posts';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
    },
    navLine: {
      marginBottom: 0,
      color: `${colors.noirBlur}`,
      backgroundColor: `${colors.noirBlur}`,
    },
    image: {
      maxWidth: '100%',
      height: 'auto',
      borderRadius: 5,
      objectFit: 'cover',
    },
    recentPostBox: {
      [theme.breakpoints.up('sm')]: {
        padding: '0 67px 60px',
      },
    },
    hr: {
      margin: '30px 0',
      color: colors.lightGray1,
      backgroundColor: colors.lightGray1,
      borderColor: colors.lightGray1,
      borderStyle: 'solid',
    },
  }),
);

export const BlogCategoryTemplate = ({ pageContext, data }: any) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { language } = useContext(LanguageContext);
  const page = pageContext[language || 'en'] || pageContext['es'];

  /* UI hack: Reduce the perception of a 'flicker' on page load in desktop mode.
      Use minHeightShim to push the footer below the fold while waiting for the
      entries to show up on the page. Once the entries are displayed, remove the
      shim.
  */
  const [minHeightShim, setMinHeightShim] = useState(
    isMobile ? 'unset' : '80vh',
  );
  useEffect(() => {
    const timer = setTimeout(() => {
      setMinHeightShim('unset');
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const [categoryData] = data.categoryData.nodes;
  const articleData = data.articleData?.nodes?.filter(
    (node: BlogArticleFragment) =>
      node?.category?.slug === page?.slug && node.node_locale === language,
  );

  const carousel = {
    panes: categoryData?.featuredArticles?.carouselArticles
      ?.filter((node: BlogArticleFragment) => node.node_locale === language)
      .map((item: BlogArticleFragment) => {
        return {
          header: item?.category?.name?.toLocaleUpperCase(),
          subHeader: item?.title,
          slug: item?.slug,
          articlePublishDate: item?.formattedPublishDate,
          image: item?.heroImage?.file?.url,
          imageFocusHorizontal: item?.imageFocusHorizontal,
          imageFocusVertical: item?.imageFocusVertical,
          backgroundColor: item?.backgroundColor,
        };
      }),
  };

  return (
    <>
      <Helmet bodyAttributes={{ class: 'TemplatePage' }}>
        <title>
          {t('BLOG_HOME')} | {categoryData.name}
        </title>
      </Helmet>
      {!isMobile && <hr className={classes.navLine} />}
      <Container maxWidth="lg" style={{ paddingRight: 0, paddingLeft: 0 }}>
        <Categories />
      </Container>
      <Box>
        <div className={classes.root}>
          {carousel?.panes?.length ? (
            <Box>
              <Carousel
                panes={carousel?.panes}
                carouselAutoPlay={
                  categoryData?.featuredArticles?.carouselAutoPlay
                }
                carouselAutoPlayDelay={
                  categoryData?.featuredArticles?.carouselAutoPlayDelay
                }
              />
            </Box>
          ) : null}
          <Container
            maxWidth="lg"
            className={classes.recentPostBox}
            style={{ minHeight: minHeightShim }}
          >
            <RecentPosts
              title={`${t('MOST_RECENT')} ${categoryData.name} ${t('POSTS')}`}
              articleData={articleData}
              featured={categoryData?.featuredArticles}
            />
          </Container>
        </div>
      </Box>
    </>
  );
};
export default BlogCategoryTemplate;

export const query = graphql`
  query categoryPageQuery($slug: String!) {
    categoryData: allContentfulCategory(
      filter: { slug: { eq: $slug, regex: "/^((?!placeholder).)*$/i" } }
    ) {
      nodes {
        ...BlogCategory
        featuredArticles {
          carouselArticles {
            ...BlogArticle
          }
          carouselAutoPlay
          carouselAutoPlayDelay
          spotlightArticle {
            ...BlogArticle
          }
        }
      }
    }
    articleData: allContentfulArticle(
      filter: { slug: { ne: null, regex: "/^((?!placeholder).)*$/i" } }
      sort: { fields: [publishDate, updateDate], order: [DESC, DESC] }
    ) {
      nodes {
        ...BlogArticle
      }
    }
  }
`;
